import CloseIcon from './CloseIcon.jsx';
import {displayColor} from '../utils/displayColor.js';
// SolarObjectCard.jsx, detail panel for solar system objects (planets, missions, Sun)
// Rendered in RightConsole when a solar system object is selected.

const MONO = 'var(--mono)';
const API = import.meta.env.VITE_API_BASE || '/api';

const C = {
  wrap: {
    width: '100%', height: '100%',
    background: 'var(--bg)',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden', color: 'var(--txt)',
  },
  header: {
    padding: '14px 16px 10px',
    borderBottom: '1px solid var(--hair)',
    flexShrink: 0,
  },
  closeBtn: {
    background: 'none', border: 'none', color: 'var(--txt-dim)',
    fontSize: 18, cursor: 'pointer', padding: '0 2px',
    lineHeight: 1, float: 'right',
  },
  typeBadge: (color) => ({
    display: 'inline-block', padding: '2px 8px',
    borderRadius: 2, fontSize: 9, fontWeight: 700,
    letterSpacing: '0.12em', fontFamily: MONO,
    background: color + '22',
    color: color,
    border: `0.5px solid ${color}55`,
    marginBottom: 8,
  }),
  name: {
    fontSize: 16, fontWeight: 700, color: 'var(--txt-bright)',
    fontFamily: MONO, letterSpacing: '0.05em',
    lineHeight: 1.3, marginBottom: 2,
  },
  sub: {
    fontSize: 10, color: 'var(--txt-dim)', fontFamily: MONO,
    letterSpacing: '0.08em',
  },
  body: {
    flex: 1, overflowY: 'auto', padding: '12px 16px',
  },
  section: {
    marginBottom: 16,
  },
  sLabel: {
    fontSize: 9, fontFamily: MONO, letterSpacing: '0.12em',
    color: 'var(--txt-mute)', marginBottom: 8, fontWeight: 700,
  },
  row: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'baseline', marginBottom: 5, gap: 8,
  },
  rowLabel: { fontSize: 10, color: 'var(--txt-dim)', fontFamily: MONO },
  rowVal: { fontSize: 11, color: 'var(--txt)', fontFamily: MONO, fontWeight: 600, textAlign: 'right' },
  desc: {
    fontSize: 11, color: 'var(--txt-dim)', lineHeight: 1.7,
    fontFamily: 'var(--sans)',
  },
  link: {
    display: 'block', marginTop: 12, padding: '6px 10px',
    background: 'var(--bg-2)',
    border: '1px solid var(--hair-2)',
    borderRadius: 3, textDecoration: 'none',
    color: 'var(--cyan)', fontSize: 10, fontFamily: MONO,
    letterSpacing: '0.08em',
    textAlign: 'center',
  },
};

const STATUS_COLOR = {
  operational: 'var(--green)',
  cruise:      'var(--cyan)',
  extended:    'var(--accent)',
  no_ephemeris:'var(--txt-mute)',
};

const TYPE_COLOR = {
  planet:      '#60a5fa',
  star:        '#fbbf24',
  mission:     '#a78bfa',
};

function formatAU(au) {
  if (!au && au !== 0) return '-';
  if (au < 0.1) return `${(au * 149.6).toFixed(1)} M km`;
  return `${au.toFixed(3)} AU`;
}

function formatLightTime(dist_au) {
  if (!dist_au && dist_au !== 0) return '-';
  const minutes = (dist_au * 499.0) / 60;
  if (minutes < 1) return `${Math.round(minutes * 60)}s`;
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  return `${(minutes / 60).toFixed(1)} hr`;
}

export default function SolarObjectCard({ item, onClose }) {
  if (!item) return null;
  const { type, data } = item;

  const typeColor = displayColor(TYPE_COLOR[type]) || 'var(--txt-dim)';
  const typeLabel = type === 'star' ? 'STAR' : type === 'planet' ? 'PLANET' : 'MISSION';

  const pos = data.position;
  const distAU = pos?.dist_au;

  return (
    <div style={C.wrap}>
      {/* Header */}
      <div style={C.header}>
        <button title="Close" aria-label="Close solar object details" className="og-close-control" style={C.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div style={C.typeBadge(typeColor)}>{typeLabel}</div>
        <div style={C.name}>{data.name || data.abbr}</div>
        {data.abbr && data.name !== data.abbr && (
          <div style={C.sub}>{data.abbr}</div>
        )}
        {data.agency && <div style={C.sub}>{data.agency}</div>}
      </div>

      {/* Body */}
      <div style={C.body}>

        {/* Position data */}
        {pos && (
          <div style={C.section}>
            <div style={C.sLabel}>POSITION</div>
            <div style={C.row}>
              <span style={C.rowLabel}>DIST FROM SUN</span>
              <span style={C.rowVal}>{formatAU(distAU)}</span>
            </div>
            {type === 'mission' && (
              <div style={C.row}>
                <span style={C.rowLabel}>LIGHT TIME FROM EARTH</span>
                <span style={C.rowVal}>{formatLightTime(Math.abs(distAU - 1.0))}</span>
              </div>
            )}
            <div style={C.row}>
              <span style={C.rowLabel}>ECLIPTIC X</span>
              <span style={C.rowVal}>{pos.x_au?.toFixed(3)} AU</span>
            </div>
            <div style={C.row}>
              <span style={C.rowLabel}>ECLIPTIC Y</span>
              <span style={C.rowVal}>{pos.y_au?.toFixed(3)} AU</span>
            </div>
          </div>
        )}

        {/* Mission metadata */}
        {type === 'mission' && (
          <div style={C.section}>
            <div style={C.sLabel}>MISSION</div>
            {data.status && (
              <div style={C.row}>
                <span style={C.rowLabel}>STATUS</span>
                <span style={{ ...C.rowVal, color: STATUS_COLOR[data.status] || 'var(--txt-dim)' }}>
                  {data.status.toUpperCase()}
                </span>
              </div>
            )}
            {data.launch && (
              <div style={C.row}>
                <span style={C.rowLabel}>LAUNCH DATE</span>
                <span style={C.rowVal}>{data.launch}</span>
              </div>
            )}
            {data.location && (
              <div style={C.row}>
                <span style={C.rowLabel}>LOCATION</span>
                <span style={C.rowVal}>{data.location}</span>
              </div>
            )}
          </div>
        )}

        {/* Planet metadata */}
        {type === 'planet' && data.radius_au && (
          <div style={C.section}>
            <div style={C.sLabel}>ORBITAL DATA</div>
            <div style={C.row}>
              <span style={C.rowLabel}>SEMI-MAJOR AXIS</span>
              <span style={C.rowVal}>{data.radius_au} AU</span>
            </div>
          </div>
        )}

        {/* Description */}
        {data.description && (
          <div style={C.section}>
            <div style={C.sLabel}>ABOUT</div>
            <div style={C.desc}>{data.description}</div>
          </div>
        )}

        {/* External link */}
        {data.url && (
          <a href={data.url} target="_blank" rel="noopener noreferrer" style={C.link}>
            NASA MISSION PAGE ↗
          </a>
        )}
      </div>
    </div>
  );
}
